import { saveExercise, unsaveExercise } from "../../utils/saveExercise";
import BookmarkButton from './BookmarkButton';
import PlaceholderImage from '../PlaceholderImage';
import { MUSCLE_GROUP_MAP } from "../../utils/muscleGroups";
import { toast } from "react-toastify";
import { useBookmark } from "../../hooks/useBookmark";
import React, { useState } from "react";
import {
  DndContext,
  closestCenter,
  PointerSensor,
  useSensor,
  useSensors,
} from "@dnd-kit/core";
import {
  arrayMove,
  SortableContext,
  useSortable,
  verticalListSortingStrategy,
} from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import { RefreshCw, GripVertical, Plus, X, ChevronUp, ChevronDown, Shuffle } from "lucide-react";
import { AnimatePresence, motion } from "framer-motion";
import exercisesData from "../../data/exercises.json";

// Custom Tooltip Component
const CustomTooltip = ({ children, content, position = "top" }) => {
  const [isVisible, setIsVisible] = useState(false);

  const positionClasses = {
    top: "bottom-full left-1/2 transform -translate-x-1/2 mb-2",
    bottom: "top-full left-1/2 transform -translate-x-1/2 mt-2",
    left: "right-full top-1/2 transform -translate-y-1/2 mr-2",
    right: "left-full top-1/2 transform -translate-y-1/2 ml-2"
  };

  return (
    <div 
      className="relative inline-block"
      onMouseEnter={() => setIsVisible(true)}
      onMouseLeave={() => setIsVisible(false)}
    >
      {children}
      {isVisible && (
        <div className={`absolute z-50 px-3 py-2 bg-gray-900 text-white text-sm rounded-lg shadow-lg whitespace-nowrap ${positionClasses[position]}`}>
          {content}
          <div className={`absolute w-2 h-2 bg-gray-900 transform rotate-45 ${
            position === 'top' ? 'top-full left-1/2 -translate-x-1/2 -mt-1' :
            position === 'bottom' ? 'bottom-full left-1/2 -translate-x-1/2 -mb-1' :
            position === 'left' ? 'left-full top-1/2 -translate-y-1/2 -ml-1' :
            'right-full top-1/2 -translate-y-1/2 -mr-1'
          }`}></div>
        </div>
      )}
    </div>
  );
};

// Drag handle
const DragHandle = ({ listeners }) => (
  <div {...listeners} className="cursor-grab text-gray-600 hover:text-gray-800 transition-colors">
    <GripVertical className="w-5 h-5" />
  </div>
);

// Sortable Exercise Card
const SortableExercise = ({
  exercise,
  index,
  expanded,
  toggleExpand,
  handleSwap,
  handleRemoveExercise,
  handleSaveExercise,
  savedExerciseIds,
  user,
  onMoveUp,
  onMoveDown,
  totalExercises,
}) => {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id: exercise.id });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
  };

  return (
    <div className="flex items-start gap-4">
      {/* Number Column */}
      <div className="w-10 flex justify-end pt-4">
        <div className="text-3xl font-bold text-gray-400">{index + 1}</div>
      </div>


      {/* Exercise Card */}
      <div
        ref={setNodeRef}
        style={style}
        {...attributes}
        className="relative flex flex-col sm:flex-row gap-3 sm:gap-4 border border-gray-300 rounded-lg p-3 sm:p-4 shadow-sm bg-white w-full"
      >
          {/* Desktop Left Rail with grey background */}
        <div className="hidden sm:block absolute top-0 left-0 bottom-0 w-10 bg-gray-200 border-r border-gray-300 rounded-l-lg">
          <div className="absolute top-4 left-2 z-10">
            <DragHandle listeners={listeners} />
          </div>
          {/* Remove Button – bottom of left rail */}
          <button
            onClick={() => handleRemoveExercise(index)}
            className="absolute bottom-2 left-2 p-1 bg-gray-400 hover:bg-gray-500 text-white rounded-full transition-colors duration-200 z-10"
          >
            <X className="w-4 h-4" />
          </button>
        </div>

        {/* Mobile Reorder Buttons – top-left corner */}
        <div className="absolute top-2 left-2 flex flex-col gap-1 sm:hidden z-10">
          <button
            onClick={() => onMoveUp(index)}
            disabled={index === 0}
            className={`p-1 rounded ${
              index === 0 
                ? 'bg-gray-100 text-gray-400 cursor-not-allowed' 
                : 'bg-gray-200 hover:bg-gray-300 text-gray-600'
            } transition-colors`}
          >
            <ChevronUp className="w-3 h-3" />
          </button>
          <button
            onClick={() => onMoveDown(index)}
            disabled={index === totalExercises - 1}
            className={`p-1 rounded ${
              index === totalExercises - 1 
                ? 'bg-gray-100 text-gray-400 cursor-not-allowed' 
                : 'bg-gray-200 hover:bg-gray-300 text-gray-600'
            } transition-colors`}
          >
            <ChevronDown className="w-3 h-3" />
          </button>
        </div>
          {/* Save Button – top-right corner */}
          <div className="absolute top-2 right-2">
            <BookmarkButton
              isSaved={savedExerciseIds.includes(exercise.id)}
              onToggle={() => handleSaveExercise(exercise)}
            />
          </div>



        {/* Image */}
        {exercise.images[0] === "placeholder.jpg" ? (
          <PlaceholderImage 
            className="w-20 h-20 sm:w-24 sm:h-24 rounded ml-0 sm:ml-12 self-center sm:self-start"
          />
        ) : (
          <img
            src={`/exercise-images/${exercise.images[0]}`}
            alt={exercise.name}
            className="w-20 h-20 sm:w-24 sm:h-24 object-cover rounded ml-0 sm:ml-12 self-center sm:self-start"
          />
        )}

        {/* Content */}
        <div className="flex-1 space-y-2">
          <div>
            <h3 className="font-semibold text-lg">{exercise.name}</h3>
          </div>

          <p className="text-sm text-gray-600">Primary Muscle Group: {exercise.primaryMuscles
            .map((muscle) =>
              muscle
                .split(" ")
                .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
                .join(" ")
            )
            .join(", ")}</p>
          {exercise.repRange && (
            <div className="text-sm text-gray-600">
              <p className="mb-1">Suggested Training:</p>
              <div className="flex flex-wrap gap-1">
                {Object.entries(exercise.repRange).map(([goal, range]) => {
                  const goalIcons = {
                    'strength': '💪',
                    'muscle_building': '🏋️',
                    'endurance': '🏃',
                    'power': '⚡',
                    'flexibility': '🧘',
                    'mobility': '🔄',
                    'recovery': '😌',
                    'cardio': '❤️',
                    'hiit': '🔥',
                    'conditioning': '⚡',
                    'explosive': '💥',
                    'technique': '🎯',
                    'stability': '⚖️',
                    'activation': '🔋'
                  };
                  
                  const goalLabels = {
                    'strength': 'Strength',
                    'muscle_building': 'Muscle Building',
                    'endurance': 'Endurance',
                    'power': 'Power',
                    'flexibility': 'Flexibility',
                    'mobility': 'Mobility',
                    'recovery': 'Recovery',
                    'cardio': 'Cardio',
                    'hiit': 'HIIT',
                    'conditioning': 'Conditioning',
                    'explosive': 'Explosive',
                    'technique': 'Technique',
                    'stability': 'Stability',
                    'activation': 'Activation'
                  };
                  
                  return (
                    <CustomTooltip
                      key={goal}
                      content={goalLabels[goal] || goal}
                      position="top"
                    >
                      <span className="text-xs px-2 py-1 bg-green-100 text-green-800 rounded-full flex items-center gap-1 hover:bg-green-200 transition-colors cursor-pointer">
                        <span>{goalIcons[goal] || '💪'}</span>
                        <span>{range}</span>
                      </span>
                    </CustomTooltip>
                  );
                })}
              </div>
            </div>
          )}
          <p className="text-sm text-gray-600 capitalize">Level: {exercise.level}</p>

          <button
            onClick={() => toggleExpand(index)}
            className="text-blue-600 text-sm hover:underline text-left"
          >
            {expanded === index ? "Collapse Instructions" : "Expand Instructions"}
          </button>

          {/* Mobile Action Buttons */}
          <div className="flex flex-col gap-2 mt-3 sm:hidden">
            {/* Shuffle Exercise Button */}
            <button
              onClick={() => handleSwap(index)}
              className="flex items-center gap-2 text-sm text-gray-700 hover:text-gray-900 border border-gray-300 rounded px-3 py-2 hover:bg-gray-50 transition-colors"
            >
              <Shuffle className="w-4 h-4" />
              Shuffle Exercise
            </button>

            {/* Remove Exercise Button */}
            <button
              onClick={() => handleRemoveExercise(index)}
              className="flex items-center gap-2 text-sm text-red-600 hover:text-red-800 border border-red-300 rounded px-3 py-2 hover:bg-red-50 transition-colors"
            >
              <X className="w-4 h-4" />
              Remove Exercise
            </button>
          </div>


          <AnimatePresence>
            {expanded === index && (
              <motion.div
                initial={{ opacity: 0, height: 0 }}
                animate={{ opacity: 1, height: "auto" }}
                exit={{ opacity: 0, height: 0 }}
                className="mt-2 text-sm text-gray-700 overflow-hidden"
              >
                <h4 className="font-medium mb-1">Instructions</h4>
                <ol className="space-y-2">
                  {exercise.instructions.map((step, i) => (
                    <li key={i} className="flex items-start gap-2">
                      <span className="min-w-[1.5rem] font-semibold">{i + 1}.</span>
                      <span>{step.trim()}</span>
                    </li>
                  ))}
                </ol>
              </motion.div>
            )}
          </AnimatePresence>
        </div>

        {/* Desktop Swap Button – bottom-right corner */}
        <button
          onClick={() => handleSwap(index)}
          className="hidden sm:flex absolute bottom-2 right-2 items-center gap-1 border border-gray-300 rounded px-3 py-1 text-sm text-gray-700 hover:bg-gray-100 bg-white shadow-sm z-10"
        >
          <Shuffle className="w-4 h-4" />
          Shuffle Exercise
        </button>
      </div>
    </div>
  );
};

const WorkoutPreview = ({
  exercises = [],
  setExercises = () => {},
  handleStart,
  handleSwap,
  user,
  savedExerciseIds = [],
  setSavedExerciseIds = () => {},
  selectedMuscles = [],
}) => {
  const [expanded, setExpanded] = useState(null);

  // Use custom bookmark hook
  const { handleSaveExercise } = useBookmark(user, savedExerciseIds, setSavedExerciseIds);

  const handleRemoveExercise = (index) => {
    const newExercises = exercises.filter((_, i) => i !== index);
    setExercises(newExercises);
    toast.info("Exercise removed from workout.");
  };

  const handleMoveUp = (index) => {
    if (index > 0) {
      const newExercises = [...exercises];
      [newExercises[index - 1], newExercises[index]] = [newExercises[index], newExercises[index - 1]];
      setExercises(newExercises);
    }
  };

  const handleMoveDown = (index) => {
    if (index < exercises.length - 1) {
      const newExercises = [...exercises];
      [newExercises[index], newExercises[index + 1]] = [newExercises[index + 1], newExercises[index]];
      setExercises(newExercises);
    }
  };

  const handleAddExercise = () => {
    // Get exercises that match the current filters
    const allowedMuscles = selectedMuscles.flatMap((group) => {
      return MUSCLE_GROUP_MAP[group] || [];
    });

    // Get current filters from localStorage
    const currentCategory = localStorage.getItem("selectedCategory");
    const currentLevel = localStorage.getItem("selectedLevel");
    const category = currentCategory ? JSON.parse(currentCategory) : null;
    const level = currentLevel ? JSON.parse(currentLevel) : null;

    const availableExercises = exercisesData.filter((ex) => {
      const matchesMuscle = selectedMuscles.length === 0 || 
        ex.primaryMuscles.some((muscle) => allowedMuscles.includes(muscle));
      const matchesCategory = category ? ex.category?.toLowerCase() === category.toLowerCase() : true;
      const matchesLevel = level ? ex.level?.toLowerCase() === level.toLowerCase() : true;
      const notInWorkout = !exercises.some((w) => w.id === ex.id);
      return matchesMuscle && matchesCategory && matchesLevel && notInWorkout;
    });

    if (availableExercises.length === 0) {
      toast.error("No more exercises available matching your criteria.");
      return;
    }

    const randomExercise = availableExercises[Math.floor(Math.random() * availableExercises.length)];
    setExercises([...exercises, randomExercise]);
    toast.success(`${randomExercise.name} added to workout.`);
  };

  const toggleExpand = (index) => {
    setExpanded(expanded === index ? null : index);
  };

  const sensors = useSensors(useSensor(PointerSensor));

  const handleDragEnd = (event) => {
    const { active, over } = event;
    if (!active || !over) return;

    if (active.id !== over.id) {
      const oldIndex = exercises.findIndex((e) => e.id === active.id);
      const newIndex = exercises.findIndex((e) => e.id === over.id);

      if (oldIndex !== -1 && newIndex !== -1) {
        setExercises(arrayMove(exercises, oldIndex, newIndex));
      }
    }
  };

  return (
    <div className="space-y-6">
      <h2 className="text-2xl text-center font-bold mb-4">
        {selectedMuscles.length > 0
          ? `Your Workout Preview for ${selectedMuscles.map(
              (group) => group.charAt(0).toUpperCase() + group.slice(1)
            ).join(", ")}`
          : "Your Workout Preview"}
      </h2>

      {exercises.length === 0 ? (
        <p className="text-center text-gray-500">
          No exercises found. Please try again.
        </p>
      ) : (
        <DndContext
          sensors={sensors}
          collisionDetection={closestCenter}
          onDragEnd={handleDragEnd}
        >
          <SortableContext
            items={exercises.map((e) => e.id)}
            strategy={verticalListSortingStrategy}
          >
            <div className="space-y-4">
              {exercises.map((exercise, index) => (
                <SortableExercise
                  key={exercise.id}
                  exercise={exercise}
                  index={index}
                  expanded={expanded}
                  toggleExpand={toggleExpand}
                  handleSwap={handleSwap}
                  handleRemoveExercise={handleRemoveExercise}
                  handleSaveExercise={handleSaveExercise}
                  user={user}
                  savedExerciseIds={savedExerciseIds}
                  onMoveUp={handleMoveUp}
                  onMoveDown={handleMoveDown}
                  totalExercises={exercises.length}
                />
              ))}
            </div>
          </SortableContext>
        </DndContext>
      )}

      {/* Add Exercise and Start Workout Buttons */}
      <div className="flex justify-end items-center gap-4">
        <button
          onClick={handleAddExercise}
          className="px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg transition duration-200 flex items-center gap-2"
        >
          <Plus className="w-4 h-4" />
          Add Another Exercise to this Workout
        </button>
        
        <button
          onClick={handleStart}
          className="px-6 py-2 bg-green-600 hover:bg-green-700 text-white rounded-lg transition duration-200"
        >
          Confirm this Workout and Begin
        </button>
      </div>
    </div>
  );
};

export default WorkoutPreview;
